import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export interface AgeRestrictions {
  accountStatus: 'minor_private' | 'standard' | 'blocked' | null;
  age: number | null;
  isMinor: boolean;
  canReceiveDMs: boolean;
  showInSearch: boolean;
  canMakePublic: boolean;
  loading: boolean;
}

export function useAgeRestrictions() {
  const { user } = useAuth();
  const [restrictions, setRestrictions] = useState<AgeRestrictions>({
    accountStatus: null,
    age: null,
    isMinor: false,
    canReceiveDMs: true,
    showInSearch: true,
    canMakePublic: true,
    loading: true,
  });

  useEffect(() => {
    const fetchAgeRestrictions = async () => {
      if (!user) {
        setRestrictions({
          accountStatus: null,
          age: null,
          isMinor: false,
          canReceiveDMs: true,
          showInSearch: true,
          canMakePublic: true,
          loading: false,
        });
        return;
      }

      try {
        // Fetch user data including DOB and account status
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('date_of_birth, account_status')
          .eq('id', user.id)
          .single();

        if (userError) throw userError;

        // Fetch profile settings
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('can_receive_dms, show_in_search, privacy_mode')
          .eq('id', user.id)
          .single();

        if (profileError) throw profileError;

        // Calculate age if DOB exists
        let age: number | null = null;
        let isMinor = false;
        let canMakePublic = true;

        if (userData.date_of_birth) {
          const dob = new Date(userData.date_of_birth);
          const today = new Date();
          age = today.getFullYear() - dob.getFullYear() - 
            (today < new Date(today.getFullYear(), dob.getMonth(), dob.getDate()) ? 1 : 0);
          
          isMinor = age >= 13 && age <= 15;
          canMakePublic = age >= 16;
        }

        setRestrictions({
          accountStatus: userData.account_status as 'minor_private' | 'standard' | 'blocked',
          age,
          isMinor,
          canReceiveDMs: profileData?.can_receive_dms ?? true,
          showInSearch: profileData?.show_in_search ?? true,
          canMakePublic,
          loading: false,
        });
      } catch (error) {
        console.error('Error fetching age restrictions:', error);
        setRestrictions(prev => ({ ...prev, loading: false }));
      }
    };

    fetchAgeRestrictions();
  }, [user]);

  return restrictions;
}
